import { mudTextToHtml, normalizeNewlines } from "./color";
import {
  CLIENT_BUILD,
  type ClientPrefs,
  aliasesToText,
  defaultWsUrl,
  ensureProfile,
  expandAlias,
  exportPrefsJson,
  getActiveChar,
  importPrefsJson,
  listProfiles,
  loadPrefs,
  pushHistory,
  savePrefs,
  textToAliases,
} from "./prefs";

const MAX_STREAM_CHARS = 400_000;
const RECONNECT_MAX_MS = 30_000;
const RECONNECT_MAX_TRIES = 12;

type ConnState = "disconnected" | "connecting" | "connected" | "reconnecting";

const els = {
  app: document.getElementById("app") as HTMLElement,
  output: document.getElementById("output") as HTMLElement,
  stream: document.getElementById("stream") as HTMLElement,
  banner: document.getElementById("banner") as HTMLElement,
  form: document.getElementById("input-form") as HTMLFormElement,
  command: document.getElementById("command") as HTMLInputElement,
  btnConnect: document.getElementById("btn-connect") as HTMLButtonElement,
  btnDisconnect: document.getElementById("btn-disconnect") as HTMLButtonElement,
  btnSend: document.getElementById("btn-send") as HTMLButtonElement,
  btnSettings: document.getElementById("btn-settings") as HTMLButtonElement,
  btnClear: document.getElementById("btn-clear") as HTMLButtonElement,
  btnJump: document.getElementById("btn-jump") as HTMLButtonElement,
  passwordMode: document.getElementById("password-mode") as HTMLInputElement,
  status: document.getElementById("status") as HTMLElement,
  profileLabel: document.getElementById("profile-label") as HTMLElement,
  buildStamp: document.getElementById("build-stamp") as HTMLElement,
  dialog: document.getElementById("settings-dialog") as HTMLDialogElement,
  settingsForm: document.getElementById("settings-form") as HTMLFormElement,
  setWsUrl: document.getElementById("set-ws-url") as HTMLInputElement,
  setAutoReconnect: document.getElementById("set-auto-reconnect") as HTMLInputElement,
  setFontSize: document.getElementById("set-font-size") as HTMLSelectElement,
  setHighContrast: document.getElementById("set-high-contrast") as HTMLInputElement,
  setTimestamps: document.getElementById("set-timestamps") as HTMLInputElement,
  setExitHotkeys: document.getElementById("set-exit-hotkeys") as HTMLInputElement,
  setProfile: document.getElementById("set-profile") as HTMLSelectElement,
  setNewProfile: document.getElementById("set-new-profile") as HTMLInputElement,
  btnAddProfile: document.getElementById("btn-add-profile") as HTMLButtonElement,
  setAliases: document.getElementById("set-aliases") as HTMLTextAreaElement,
  setGlobalAliases: document.getElementById("set-global-aliases") as HTMLTextAreaElement,
  btnExport: document.getElementById("btn-export") as HTMLButtonElement,
  btnImport: document.getElementById("btn-import") as HTMLButtonElement,
  importFile: document.getElementById("import-file") as HTMLInputElement,
};

let prefs: ClientPrefs = loadPrefs();
// Persist migrations (e.g. v2: disable letter hotkeys, seed n/s/e/w aliases).
savePrefs(prefs);
let socket: WebSocket | null = null;
let historyIndex = -1;
let draft = "";
let recentPlain = "";
let stickToBottom = true;
let userClosed = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let connState: ConnState = "disconnected";

/** Arrow keys only — never letter keys (those break typing names like "Sally"). */
const ARROW_EXITS: Record<string, string> = {
  ArrowUp: "north",
  ArrowDown: "south",
  ArrowRight: "east",
  ArrowLeft: "west",
  // PageUp / PageDown as up/down if desired:
  // PageUp: "up",
  // PageDown: "down",
};

// --- prefs → DOM ---

function applyDisplayPrefs(): void {
  document.documentElement.dataset.font = prefs.fontSize;
  document.documentElement.classList.toggle("high-contrast", prefs.highContrast);
  els.profileLabel.textContent = `Profile: ${prefs.activeProfile}`;
  els.buildStamp.textContent = `client ${CLIENT_BUILD}`;
}

function persist(): void {
  savePrefs(prefs);
}

function historyList(): string[] {
  return getActiveChar(prefs).history;
}

// --- UI helpers ---

function setStatus(state: ConnState): void {
  connState = state;
  els.status.textContent = state;
  els.status.className = `status ${state}`;
  const live = state === "connected";
  const busy = state === "connecting" || state === "reconnecting";
  els.command.disabled = !live;
  els.btnSend.disabled = !live;
  els.btnConnect.disabled = live || busy;
  els.btnDisconnect.disabled = state === "disconnected";
}

function showBanner(msg: string, kind: "info" | "warn" | "error" = "info"): void {
  els.banner.textContent = msg;
  els.banner.className = `banner ${kind}`;
  els.banner.classList.remove("hidden");
}

function hideBanner(): void {
  els.banner.classList.add("hidden");
  els.banner.textContent = "";
}

function isNearBottom(): boolean {
  const el = els.output;
  return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
}

function scrollBottom(force = false): void {
  if (force || stickToBottom) {
    els.output.scrollTop = els.output.scrollHeight;
    stickToBottom = true;
    els.btnJump.classList.add("hidden");
  }
}

function updateJumpButton(): void {
  stickToBottom = isNearBottom();
  els.btnJump.classList.toggle("hidden", stickToBottom);
}

function trimStream(): void {
  while (els.stream.textContent && els.stream.textContent.length > MAX_STREAM_CHARS) {
    const first = els.stream.firstChild;
    if (!first) break;
    els.stream.removeChild(first);
  }
}

function stampPrefix(): string {
  if (!prefs.showTimestamps) return "";
  const d = new Date();
  const t = d.toLocaleTimeString(undefined, { hour12: false });
  return `[${t}] `;
}

function appendMudHtml(html: string): void {
  if (!html) return;
  const wasSticky = stickToBottom;
  els.stream.insertAdjacentHTML("beforeend", html);
  trimStream();
  if (wasSticky) scrollBottom(true);
  else updateJumpButton();
}

function sys(msg: string): void {
  const div = document.createElement("div");
  div.className = "note sys";
  div.textContent = `${stampPrefix()}${msg}`;
  const wasSticky = stickToBottom;
  els.stream.appendChild(div);
  if (wasSticky) scrollBottom(true);
  else updateJumpButton();
}

function echoLine(text: string): void {
  const div = document.createElement("div");
  div.className = "note echo";
  div.textContent = text;
  const wasSticky = stickToBottom;
  els.stream.appendChild(div);
  if (wasSticky) scrollBottom(true);
}

function applyPasswordMode(): void {
  els.command.type = els.passwordMode.checked ? "password" : "text";
}

function maybeAutoPasswordMode(plainChunk: string): void {
  recentPlain = (recentPlain + plainChunk).slice(-400);
  if (/password/i.test(recentPlain) && /:\s*$/m.test(recentPlain.slice(-80))) {
    if (!els.passwordMode.checked) {
      els.passwordMode.checked = true;
      applyPasswordMode();
    }
  }
}

function decodeMessage(data: unknown): string {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) {
    return new TextDecoder("latin1").decode(data);
  }
  if (ArrayBuffer.isView(data)) {
    return new TextDecoder("latin1").decode(data as ArrayBufferView);
  }
  return String(data);
}

function clearReconnectTimer(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect(): void {
  if (!prefs.autoReconnect || userClosed) return;
  if (reconnectAttempt >= RECONNECT_MAX_TRIES) {
    showBanner("Auto-reconnect gave up. Click Connect to try again.", "warn");
    setStatus("disconnected");
    return;
  }
  const delay = Math.min(1000 * 2 ** reconnectAttempt, RECONNECT_MAX_MS);
  reconnectAttempt++;
  setStatus("reconnecting");
  showBanner(`Reconnecting in ${Math.round(delay / 1000)}s (attempt ${reconnectAttempt})…`, "warn");
  clearReconnectTimer();
  reconnectTimer = setTimeout(() => {
    connect(true);
  }, delay);
}

// --- connection ---

function connect(isRetry = false): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }

  const url = prefs.wsUrl.trim() || defaultWsUrl();
  prefs.wsUrl = url;
  persist();

  userClosed = false;
  clearReconnectTimer();
  setStatus(isRetry ? "reconnecting" : "connecting");
  if (!isRetry) {
    hideBanner();
    sys(`Connecting to ${url} …`);
  }

  try {
    socket = new WebSocket(url);
  } catch (err) {
    sys(`Failed to open WebSocket: ${(err as Error).message}`);
    showBanner("Could not open WebSocket. Check the URL in Settings.", "error");
    setStatus("disconnected");
    scheduleReconnect();
    return;
  }

  socket.binaryType = "arraybuffer";

  socket.addEventListener("open", () => {
    reconnectAttempt = 0;
    setStatus("connected");
    hideBanner();
    sys(isRetry ? "Reconnected." : "Connected to builder via gateway.");
    showBanner("Connected", "info");
    setTimeout(() => {
      if (connState === "connected") hideBanner();
    }, 2000);
    els.command.focus();
  });

  socket.addEventListener("message", (ev) => {
    const raw = decodeMessage(ev.data);
    const plain = normalizeNewlines(raw);
    maybeAutoPasswordMode(plain);
    appendMudHtml(mudTextToHtml(raw));
  });

  socket.addEventListener("close", (ev) => {
    socket = null;
    const reason = ev.reason || (ev.code ? `code ${ev.code}` : "");
    if (userClosed) {
      sys(`Disconnected${reason ? ` (${reason})` : ""}.`);
      setStatus("disconnected");
      hideBanner();
      return;
    }
    sys(`Connection closed${reason ? ` (${reason})` : ""}.`);
    if (prefs.autoReconnect) {
      scheduleReconnect();
    } else {
      setStatus("disconnected");
      showBanner("Disconnected. Click Connect to try again.", "warn");
    }
  });

  socket.addEventListener("error", () => {
    // close handler will fire; avoid double messaging
    if (!isRetry) {
      sys("WebSocket error — is the gateway up? (mdhoria-gateway on the VPS)");
    }
  });
}

function disconnect(): void {
  userClosed = true;
  clearReconnectTimer();
  reconnectAttempt = 0;
  if (socket) {
    socket.close(1000, "client disconnect");
    socket = null;
  }
  setStatus("disconnected");
  hideBanner();
  sys("Disconnected.");
}

function sendLineClean(raw: string): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    sys("Not connected.");
    return;
  }

  const typed = raw.replace(/\r?\n$/, "");
  const password = els.passwordMode.checked;
  let toSend = typed;

  if (!password) {
    const { line: expanded, expanded: did, from } = expandAlias(typed, prefs);
    if (did) {
      echoLine(`» ${from} → ${expanded}`);
      toSend = expanded;
    } else if (typed.length > 0) {
      echoLine(`» ${typed}`);
    }
    if (typed.length > 0) {
      pushHistory(prefs, typed);
      persist();
    }
  } else {
    echoLine("» ********");
  }

  socket.send(toSend);
  historyIndex = -1;
  draft = "";
  els.command.value = "";
  stickToBottom = true;
  scrollBottom(true);
}

// --- settings dialog ---

function fillProfileSelect(): void {
  const names = listProfiles(prefs);
  els.setProfile.innerHTML = "";
  for (const n of names) {
    const opt = document.createElement("option");
    opt.value = n;
    opt.textContent = n;
    if (n === prefs.activeProfile) opt.selected = true;
    els.setProfile.appendChild(opt);
  }
}

function loadAliasesIntoForm(): void {
  const ch = getActiveChar(prefs);
  els.setAliases.value = aliasesToText(ch.aliases);
  els.setGlobalAliases.value = aliasesToText(prefs.globalAliases);
}

function openSettings(): void {
  els.setWsUrl.value = prefs.wsUrl;
  els.setAutoReconnect.checked = prefs.autoReconnect;
  els.setFontSize.value = prefs.fontSize;
  els.setHighContrast.checked = prefs.highContrast;
  els.setTimestamps.checked = prefs.showTimestamps;
  els.setExitHotkeys.checked = prefs.exitHotkeys;
  fillProfileSelect();
  loadAliasesIntoForm();
  els.dialog.showModal();
}

function saveSettingsFromForm(): void {
  prefs.wsUrl = els.setWsUrl.value.trim() || defaultWsUrl();
  prefs.autoReconnect = els.setAutoReconnect.checked;
  prefs.fontSize = els.setFontSize.value as ClientPrefs["fontSize"];
  prefs.highContrast = els.setHighContrast.checked;
  prefs.showTimestamps = els.setTimestamps.checked;
  prefs.exitHotkeys = els.setExitHotkeys.checked;

  const profile = els.setProfile.value || prefs.activeProfile;
  prefs.activeProfile = profile;
  ensureProfile(prefs, profile);
  getActiveChar(prefs).aliases = textToAliases(els.setAliases.value);
  prefs.globalAliases = textToAliases(els.setGlobalAliases.value);

  persist();
  applyDisplayPrefs();
  sys(`Settings saved (profile “${prefs.activeProfile}”).`);
}

function addProfile(): void {
  const name = els.setNewProfile.value.trim().slice(0, 32);
  if (!name) return;
  ensureProfile(prefs, name);
  prefs.activeProfile = name;
  // capture current textarea into previous profile first? use form profile select
  const prev = els.setProfile.value;
  if (prev) {
    ensureProfile(prefs, prev).aliases = textToAliases(els.setAliases.value);
  }
  els.setNewProfile.value = "";
  fillProfileSelect();
  loadAliasesIntoForm();
  persist();
}

function exportPrefs(): void {
  const blob = new Blob([exportPrefsJson(prefs)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `mdhoria-client-prefs-${prefs.activeProfile}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
  sys("Exported preferences JSON.");
}

function importPrefsFile(file: File): void {
  const reader = new FileReader();
  reader.onload = () => {
    try {
      const text = String(reader.result ?? "");
      prefs = importPrefsJson(text);
      persist();
      applyDisplayPrefs();
      fillProfileSelect();
      loadAliasesIntoForm();
      sys("Imported preferences.");
      showBanner("Preferences imported", "info");
      setTimeout(hideBanner, 2500);
    } catch (e) {
      sys(`Import failed: ${(e as Error).message}`);
      showBanner("Import failed — invalid JSON?", "error");
    }
  };
  reader.readAsText(file);
}

// --- wire up ---

applyDisplayPrefs();
setStatus("disconnected");
applyPasswordMode();
sys(`Web client ${CLIENT_BUILD} ready — prefs in this browser (export to back up).`);

els.output.addEventListener("scroll", () => updateJumpButton(), { passive: true });
els.btnJump.addEventListener("click", () => scrollBottom(true));
els.btnClear.addEventListener("click", () => {
  els.stream.replaceChildren();
  sys("Scrollback cleared.");
});

els.btnConnect.addEventListener("click", () => connect(false));
els.btnDisconnect.addEventListener("click", () => disconnect());
els.passwordMode.addEventListener("change", () => applyPasswordMode());
els.btnSettings.addEventListener("click", () => openSettings());

els.settingsForm.addEventListener("submit", (e) => {
  e.preventDefault();
  const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
  const value = submitter?.value ?? "cancel";
  if (value === "save") {
    saveSettingsFromForm();
  }
  els.dialog.close();
});

els.setProfile.addEventListener("change", () => {
  // save current textarea to previous profile, switch
  const prev = prefs.activeProfile;
  ensureProfile(prefs, prev).aliases = textToAliases(els.setAliases.value);
  prefs.activeProfile = els.setProfile.value;
  ensureProfile(prefs, prefs.activeProfile);
  loadAliasesIntoForm();
  applyDisplayPrefs();
});

els.btnAddProfile.addEventListener("click", () => addProfile());
els.btnExport.addEventListener("click", () => exportPrefs());
els.btnImport.addEventListener("click", () => els.importFile.click());
els.importFile.addEventListener("change", () => {
  const f = els.importFile.files?.[0];
  if (f) importPrefsFile(f);
  els.importFile.value = "";
});

els.form.addEventListener("submit", (e) => {
  e.preventDefault();
  sendLineClean(els.command.value);
});

els.command.addEventListener("keydown", (e) => {
  const hist = historyList();
  const empty = els.command.value === "";

  // Optional: arrow keys as movement only when input is empty (never letter keys).
  if (
    prefs.exitHotkeys &&
    empty &&
    !els.passwordMode.checked &&
    !e.ctrlKey &&
    !e.metaKey &&
    !e.altKey &&
    ARROW_EXITS[e.key]
  ) {
    e.preventDefault();
    sendLineClean(ARROW_EXITS[e.key]!);
    return;
  }

  // History: arrows when there is text, or when exit-hotkeys are off
  if (e.key === "ArrowUp" && (!empty || !prefs.exitHotkeys)) {
    e.preventDefault();
    if (hist.length === 0) return;
    if (historyIndex === -1) {
      draft = els.command.value;
      historyIndex = hist.length - 1;
    } else if (historyIndex > 0) {
      historyIndex--;
    }
    els.command.value = hist[historyIndex] ?? "";
  } else if (e.key === "ArrowDown" && (!empty || !prefs.exitHotkeys)) {
    e.preventDefault();
    if (historyIndex === -1) return;
    if (historyIndex < hist.length - 1) {
      historyIndex++;
      els.command.value = hist[historyIndex] ?? "";
    } else {
      historyIndex = -1;
      els.command.value = draft;
    }
  }
});

